import {
  CalendarClock, Circle, CircleCheck, Ear, Headphones, Megaphone, MoreHorizontal, PhoneForwarded, RotateCcw,
  Send, Undo2, UsersRound,
} from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Callout, Spinner } from "@/components/ui/feedback";
import { Tooltip } from "@/components/ui/misc";
import { Input, Textarea } from "@/components/ui/input";
import { useToast } from "@/components/ui/toast";
import { useBackupTime } from "@/features/callback-backup";
import { useCallbackMinutes } from "@/features/callback-minutes";
import { useCallbackTime } from "@/features/callback-picker";
import { AlsoBookFor } from "@/features/also-book-for";
import { CallbackTimes } from "@/features/callback-times";
import { alsoBookedMessage, type AlsoBooking } from "@/lib/also-book";
import { type CallType, CallTypeChip, callTypeOf, knownCallType } from "@/features/call-type";
import { OutcomePicker } from "@/features/outcome-picker";
import { caseOptionLabel, casesOnTheNumber } from "@/lib/cases";
import { leadName } from "@/lib/campaign";
import {
  acdCall, callKindText, campaignAnswers, campaignTakes, consultBriefing, dialedAnswer, forcedAnswer,
  pendingDisposition, repNumberText, type AcdCall, type Briefing,
} from "@/lib/acd";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CALLING_BACK, DROPPED_OUTCOME } from "@/lib/call-back";
import { backupGapMinutes, backupTimesWhen, backupsKept, callbackWhen, keptBackups, wallTime } from "@/lib/callbacks";
import { SHORT_CALL_SECONDS, suggestedOutcomes } from "@/lib/dispositions";
import {
  CLOSER_SEARCH_FROM, closerName, closerRows, heldChips, matchClosers, ownCallsHelp, sendHelp,
  skillChips, unreached,
} from "@/lib/handoff";
import {
  currentMonitors, endedMonitors, forgetMonitor, joiningListenIn, MONITOR_MODES, monitorAsked,
  monitorToAnswer, setMonitorMode, subscribeMonitors, type MonitorMode, type Monitoring,
} from "@/lib/monitor";
import {
  useAcdMe, useAgentAction, useCallDetection, useCallDispositions, useCallHold, useColleagues,
  useDialerCall,
  useDispositionCall, useFinishConsult, useHandoff, useLeadCard, useMonitorMode, useMyAcdSessions, useMyOutcomes,
  useStagesWithCadence, useSetCallBackMemory, type ConsultEnding, type HandoffMode, useContactOutcome,
} from "@/lib/queries";
import type { DialerCall, Disposition, HandoffCloser, HandoffClosers } from "@/lib/types";
import { cn, formatPhoneNumber } from "@/lib/utils";
import { plannedOutcomeFor, redialHint } from "@/lib/dialer";
import {
  answersItself, ringIsFresh, selfAnswerFailed, setCampaignAnswering, subscribeRings, watchRing,
} from "@/softphone/auto-answer";
import {
  claimPlannedOutcome, forgetPlannedOutcome, usePlannedOutcome,
} from "@/softphone/planned-outcome";
import { forgetPickedCallback, usePickedCallback } from "@/softphone/picked-callback";
import { useSoftphone } from "@/softphone/softphone-context";
import { seesCampaigns } from "@/lib/types";

/**
 * The ACD's side of a call on the phone (ADR-016, ADR-017): what the rep reads before saying
 * hello, how a setter sends a caller on to a closer, and the outcome once the call ends.
 *
 * All of it reads the event-tracker's record of the rep (`GET /acd/me`), the same record the
 * availability control reads — never the phone's own idea of the call, which knows a number and
 * nothing about why it rang. `acdCall` in `lib/acd.ts` matches the two.
 */

/** The call the ACD rang this phone for, while it rings or is up; null otherwise. */
export function useAcdCall(): AcdCall | null {
  const { user } = useAuth();
  const phone = useSoftphone();
  const { data: me } = useAcdMe({ enabled: Boolean(user?.sip_endpoint_id) });
  return acdCall(me, phone.calls);
}

/** The briefing this rep is in the middle of (ADR-027), or null: their own record's word. */
export function useBriefing(): Briefing | null {
  const { user } = useAuth();
  const { data: me } = useAcdMe({ enabled: Boolean(user?.sip_endpoint_id) });
  return consultBriefing(me);
}

/** Whether the rep's campaign answers its calls for them right now (`campaignAnswers`). */
export function useCampaignAnswers(): boolean {
  const { user } = useAuth();
  const { data: me } = useAcdMe({ enabled: Boolean(user?.sip_endpoint_id) });
  return campaignAnswers(me);
}

/** How long the lead the dialer was last seen calling for the rep stays the one a ringing call is
 *  matched to. The read stops being asked for once the call is up; a dial is over long before. */
const DIAL_KEPT_MS = 20_000;

/** While a call rings that the rep's record does not name yet: how often to read the record
 *  again, and how many times. */
const CATCH_UP_EVERY_MS = 500;
const CATCH_UP_TIMES = 10;

/** Whether the ringing call `callId` is still inside the moment the phone gives itself to answer a
 *  call before it goes back to asking the rep. The clock is the call's, not this component's
 *  (`watchRing` in `softphone/auto-answer.ts`): a surface drawn late does not start it again. */
function useFreshRing(callId: string | null): boolean {
  React.useEffect(() => (callId === null ? undefined : watchRing(callId)), [callId]);
  return React.useSyncExternalStore(
    subscribeRings,
    () => callId !== null && ringIsFresh(callId),
    () => false,
  );
}

/**
 * Whether the phone is answering the ringing call `callId` by itself (`answersItself` in
 * `softphone/auto-answer.ts`): this browser's auto-answer, or the campaign's. The phone's
 * surfaces ask before they show Answer and Decline, and show the call connecting instead. Null
 * is "no call is ringing in", which is no.
 */
export function useAnsweringItself(callId: string | null): boolean {
  const { user } = useAuth();
  const phone = useSoftphone();
  const { data: me } = useAcdMe({ enabled: Boolean(user?.sip_endpoint_id) });
  const fresh = useFreshRing(callId);
  return callId !== null && answersItself(me, phone.calls, callId, phone.autoAnswer, fresh);
}

/**
 * The campaign's auto-answer. A call the ACD rings the rep for on a line whose campaign asks for it
 * is answered without a click, whatever this browser's own auto-answer says — which still decides
 * every other call, another campaign's included. `forcedAnswer` holds the rule.
 *
 * It is for the dialer: the ACD holds a lead who answered for their rep only for the campaign's
 * abandon window, two seconds by default, and a rep who has to reach for Answer misses most of
 * them. So the call cannot wait for the refetch hint about it. The switch's invite usually reaches
 * the browser before the event-tracker's record says the rep is ringing, and while a call rings
 * that the record does not name yet, on a rep signed in to a line that asks, the record is read
 * again every half second.
 *
 * The phone says one ding as it answers (`answer(id, { byItself: true })`), and does not ring for
 * the call in the moment before: the hook tells the ringer which ringing call the campaign is
 * taking or is about to take (`campaignTakes`, `setCampaignAnswering`), for as long as the phone
 * gives itself to answer a call. After that the call rings and asks, as any other.
 *
 * Mounted once, in the dock, which is on every screen.
 */
export function useCampaignAutoAnswer(): void {
  const { user } = useAuth();
  const phone = useSoftphone();
  const toast = useToast();
  const { data: me, refetch } = useAcdMe({ enabled: Boolean(user?.sip_endpoint_id) });
  const inForce = campaignAnswers(me) && !phone.autoAnswer;
  // The lead the dialer is calling for this rep, read while the dial is out and kept for the
  // moment its call rings: the call is then answered from what is already here, with no read of
  // core-api between the ring and the answer (`dialedAnswer` in `lib/acd.ts`).
  const dialing = me?.state?.state === "dialing" || me?.state?.state === "ringing";
  const { data: current, dataUpdatedAt } = useDialerCall({ enabled: inForce && dialing });
  const dial = React.useRef<{ call: DialerCall; at: number } | null>(null);
  // Remembered after the render, from the time of the read and not of the render: the query keeps
  // its last answer once it is switched off (and when a read brings the same answer back it is
  // the same object), so a write on every render would keep a finished dial fresh for good.
  React.useEffect(() => {
    if (current) dial.current = { call: current, at: dataUpdatedAt };
  }, [current, dataUpdatedAt]);
  const known =
    dial.current !== null && Date.now() - dial.current.at < DIAL_KEPT_MS ? dial.current.call : null;
  const target =
    forcedAnswer(me, phone.calls, phone.autoAnswer) ??
    dialedAnswer(me, phone.calls, known, phone.autoAnswer);
  const ringing =
    phone.calls.find((c) => c.direction === "inbound" && c.state === "ringing")?.id ?? null;
  const answered = React.useRef(new Set<string>());
  const fresh = useFreshRing(ringing);
  const taking = fresh ? campaignTakes(me, phone.calls, phone.autoAnswer) : null;
  React.useEffect(() => {
    setCampaignAnswering(taking);
    return () => setCampaignAnswering(null);
  }, [taking]);

  React.useEffect(() => {
    if (!inForce || ringing === null || target !== null) return;
    let times = 0;
    void refetch();
    const timer = window.setInterval(() => {
      times += 1;
      if (times >= CATCH_UP_TIMES) window.clearInterval(timer);
      void refetch();
    }, CATCH_UP_EVERY_MS);
    return () => window.clearInterval(timer);
  }, [inForce, ringing, target, refetch]);

  const { answer } = phone;
  React.useEffect(() => {
    if (target === null || answered.current.has(target)) return;
    answered.current.add(target);
    answer(target, { byItself: true }).then(() => {
      // The dial is spent: a later ring is not matched to it for the rest of its twenty seconds.
      dial.current = null;
    }, (e: unknown) => {
      // A refused microphone is the usual cause, and retrying does not fix it: the call is left
      // ringing for the rep to take by hand, as the browser's own auto-answer leaves it. Answer
      // and Decline come back now, not when the phone's moment runs out.
      selfAnswerFailed(target);
      console.error("[softphone] campaign auto-answer failed", e);
      toast.error("Could not answer automatically", "Answer the call yourself.");
    });
  }, [target, answer, toast]);
}

/**
 * What the phone says about the campaign side of a call: the campaign it came through, what kind
 * of call it is, and, for a caller handed over, who sent them, whether they are briefing this rep
 * right now, and their note. Null for a call that is not the ACD's.
 */
function useAcdCallFacts(callId: string): {
  /** Null for a rep, who reads no campaign (ACL-652). */
  campaign: string | null;
  closers: boolean;
  /** "Inbound call", "Called your number", "Handed off to you by Sam Ortega". */
  kind: string;
  /** The call's type for its chip (ACL-508), with what the chip does not say beside it: "Called
   *  your number", "by Sam Ortega". Null type for a call whose kind the ACD did not say. */
  type: CallType | null;
  detail: string | null;
  /** The closer's half of a warm handoff, in a sentence, while it lasts. */
  briefing: string | null;
  note: string | null;
} | null {
  const acd = useAcdCall();
  const ours = acd !== null && acd.callId === callId;
  const { data: handoff } = useHandoff(ours ? acd.handoffId : null);
  // A caller for another rep's number needs that rep's name as much as a handoff needs its sender's.
  const { data: colleagues } = useColleagues({
    enabled: Boolean(handoff) || (ours && acd.meantFor !== null),
  });
  const { data: me } = useAcdMe({ enabled: ours });
  const { user } = useAuth();
  // The closer's half of a warm handoff: they have the setter on the line, not the client yet.
  const beingBriefed = ours && consultBriefing(me)?.role === "closer";
  if (!ours) return null;

  const sender = handoff
    ? (colleagues?.find((c) => c.extension === handoff.from_user)?.name ?? handoff.from_user)
    : null;
  // A caller who dialed a rep's own number (ADR-037): "Called your number", or whose it was.
  const dialed = repNumberText(
    acd.kind,
    acd.meantFor,
    me,
    (extension) => colleagues?.find((c) => c.extension === extension)?.name,
  );
  return {
    // A rep reads no campaign or line, only that it is a closers' call (ACL-652).
    campaign: seesCampaigns(user?.role) ? (acd.line?.campaign_name ?? acd.queue ?? "One of your lines") : null,
    closers: acd.line?.purpose === "closer",
    kind: `${dialed ?? callKindText(acd.kind)}${sender ? ` by ${sender}` : ""}`,
    type: knownCallType(acd.kind),
    detail: dialed ?? (sender ? `by ${sender}` : null),
    briefing: beingBriefed
      ? `${sender ? `${sender} is briefing you` : "You are being briefed"} · the caller is on hold`
      : null,
    note: handoff?.note || null,
  };
}

/**
 * The campaign a call came through, what kind of call it is, and — for a caller handed over — who
 * sent them and their note, which is the point of a handoff: the closer does not ask the caller to
 * start again. Under the party, on the ringing screen and in the phone's own call view. The Phone
 * page's call has the same facts in two smaller places (`AcdCallChip`, `AcdHandoffNote`): a box
 * across the whole page to say a campaign's name was a lot of page for one line.
 */
export function AcdCallStrip({ callId }: { callId: string }) {
  const facts = useAcdCallFacts(callId);
  if (!facts) return null;
  return (
    <div className="w-full rounded-lg border border-border bg-surface-sunken px-3 py-2 text-left text-xs">
      {facts.campaign ? (
        <p className="flex min-w-0 items-center gap-1.5 text-text">
          <Megaphone className="size-3.5 shrink-0 text-text-muted" aria-hidden />
          <span className="truncate font-medium">{facts.campaign}</span>
          {facts.closers && <span className="shrink-0 text-text-muted">· Closers</span>}
        </p>
      ) : (
        facts.closers && <p className="truncate font-medium text-text">Closers</p>
      )}
      {facts.type ? (
        <p className="mt-1 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-text-muted">
          <CallTypeChip type={facts.type} size="sm" />
          {facts.detail && <span>{facts.detail}</span>}
        </p>
      ) : (
        <p className="mt-0.5 text-text-muted">{facts.kind}</p>
      )}
      {facts.briefing && (
        <p className="mt-0.5 flex items-center gap-1.5 text-text-muted">
          <Headphones className="size-3.5 shrink-0" aria-hidden />
          {facts.briefing}
        </p>
      )}
      {facts.note && (
        <p className="mt-1.5 whitespace-pre-line border-l-2 border-accent-8 pl-2 text-text">
          {facts.note}
        </p>
      )}
    </div>
  );
}

/**
 * The campaign and the kind of call as one small chip, for the Phone page's call bar: the name
 * of the campaign and "Inbound call" are a few words, and sit beside the party they are about.
 * It gives way first when the bar is short of room (the party's name and the call's state do not),
 * and its `title` has the whole of it.
 */
export function AcdCallChip({ callId, className }: { callId: string; className?: string }) {
  const facts = useAcdCallFacts(callId);
  if (!facts) return null;
  const campaign = facts.campaign
    ? `${facts.campaign}${facts.closers ? " · Closers" : ""}`
    : facts.closers
      ? "Closers"
      : null;
  return (
    <span
      className={cn(
        "inline-flex min-w-0 items-center gap-1.5 rounded-md border border-border bg-surface-sunken px-2 py-1 text-xs",
        className,
      )}
      title={campaign ? `${campaign} · ${facts.kind}` : facts.kind}
    >
      {facts.campaign && <Megaphone className="size-3.5 shrink-0 text-text-muted" aria-hidden />}
      {campaign && <span className="truncate font-medium text-text">{campaign}</span>}
      {facts.type ? (
        <>
          <CallTypeChip type={facts.type} size="sm" />
          {facts.detail && <span className="shrink-0 text-text-muted">{facts.detail}</span>}
        </>
      ) : (
        <span className="shrink-0 text-text-muted">· {facts.kind}</span>
      )}
    </span>
  );
}

/**
 * What a handoff brings with it, for the Phone page, beside the lead's card: that the sender is
 * briefing this rep right now, and the note they wrote. Nothing for a call nobody handed over.
 */
export function AcdHandoffNote({ callId }: { callId: string }) {
  const facts = useAcdCallFacts(callId);
  if (!facts || (facts.briefing === null && facts.note === null)) return null;
  return (
    <section
      aria-label="Handed off to you"
      className="w-full rounded-lg border border-border bg-surface-sunken px-3 py-2 text-left text-xs"
    >
      {facts.briefing && (
        <p className="flex items-center gap-1.5 text-text-muted">
          <Headphones className="size-3.5 shrink-0" aria-hidden />
          {facts.briefing}
        </p>
      )}
      {facts.note && (
        <p className={cn("whitespace-pre-line border-l-2 border-accent-8 pl-2 text-text", facts.briefing && "mt-1.5")}>
          {facts.note}
        </p>
      )}
    </section>
  );
}

/** What the setter chose on the form: a closer by name, or any closer holding these skills. */
export interface CloserChoice {
  target: HandoffCloser | null;
  needs: string[];
}

/**
 * Send to a closer. The ACD takes the caller off this rep, holds them on music, and rings a closer
 * with this note on their screen; core-api picks the campaign's closer line and the `closer`
 * skill. A section of Transfer rather than a tile of its own, because handing a caller on is one
 * intention whichever way it goes.
 *
 * The setter says who (ADR-031): any closer who is free — the ACD picks, narrowed by a skill when
 * they ask, which rides the request on top of `closer` — or one by name, from the closers on that
 * line with the ACD's state of each. Only a closer who is free can be picked; the rest are shown
 * with why, so the setter can wait for Maria or choose somebody else. A named closer bypasses the
 * skills, as any pin does, so the chips only narrow the list once a name is picked. `lib/handoff.ts`
 * holds the rules; this is the form.
 */
export function SendToCloserForm({
  busy, closers, loadingClosers, onSend,
}: {
  busy: boolean;
  /** `GET /handoffs/closers`; undefined while it loads, or when it could not be read. */
  closers: HandoffClosers | undefined;
  loadingClosers: boolean;
  onSend: (note: string, mode: HandoffMode, choice: CloserChoice) => void;
}) {
  const id = React.useId();
  const [note, setNote] = React.useState("");
  const [needs, setNeeds] = React.useState<string[]>([]);
  const [search, setSearch] = React.useState("");
  const [target, setTarget] = React.useState<HandoffCloser | null>(null);
  const skills = closers?.skills ?? [];
  const chips = skillChips(skills, closers?.closers ?? []);
  const listed = closerRows(closers?.closers ?? [], needs);
  const rows = matchClosers(listed, search, skills);
  // A closer picked who has since gone from the list, or is no longer free, is not sent to by
  // surprise: the choice falls back to any closer, and their row says why. Read off the whole
  // list, never the search: typing a name that hides the pick must not throw the pick away.
  const picked =
    target !== null &&
    listed.some((r) => r.pickable && r.closer.sip_endpoint_id === target.sip_endpoint_id)
      ? target
      : null;
  React.useEffect(() => {
    if (target !== null && picked === null) setTarget(null);
  }, [target, picked]);

  const toggleNeed = (slug: string) =>
    setNeeds((now) => (now.includes(slug) ? now.filter((s) => s !== slug) : [...now, slug]));
  const choice: CloserChoice = { target: picked, needs };
  const needsText = chips.filter((c) => needs.includes(c.slug)).map((c) => c.name).join(" and ");

  return (
    <form
      className="flex flex-col gap-1.5"
      onSubmit={(e) => {
        e.preventDefault();
        if (!busy) onSend(note, "consult", choice);
      }}
    >
      <div className="flex items-baseline justify-between gap-2">
        <p id={`${id}-who`} className="text-xs text-text-muted">
          Who takes the caller
        </p>
        {listed.length > 0 && (
          <p className="shrink-0 text-xs text-text-subtle tabular">
            {rows.length === listed.length
              ? `${listed.length} on the line`
              : `${rows.length} of ${listed.length}`}
          </p>
        )}
      </div>
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Needs a skill">
          <span className="text-xs text-text-muted">Needs</span>
          {chips.map((chip) => {
            const on = needs.includes(chip.slug);
            return (
              <button
                key={chip.slug}
                type="button"
                role="checkbox"
                aria-checked={on}
                onClick={() => toggleNeed(chip.slug)}
                className={cn(
                  "h-6 rounded-full border px-2 text-xs transition-colors",
                  on
                    ? "border-accent-8 bg-accent-3 text-accent-11"
                    : "border-border bg-surface text-text-muted hover:bg-surface-hover",
                )}
              >
                {chip.name}
              </button>
            );
          })}
        </div>
      )}
      {/* A line can staff dozens of closers. Past a handful the list is searched rather than
          scrolled — by name, extension or skill (`matchClosers`) — and the count above says how
          much of it a search is hiding. */}
      {listed.length > CLOSER_SEARCH_FROM && (
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Name, extension or skill"
          autoComplete="off"
          className="h-9 text-sm"
          aria-label="Find a closer"
        />
      )}
      <div
        role="radiogroup"
        aria-labelledby={`${id}-who`}
        className="flex max-h-64 flex-col gap-1 overflow-y-auto"
      >
        <ChoiceRow
          checked={picked === null}
          title="Any closer who is free"
          subtitle={
            needs.length > 0
              ? `The next free closer who holds ${needsText}`
              : "The ACD picks the next closer free"
          }
          onPick={() => setTarget(null)}
        />
        {loadingClosers ? (
          <p className="flex items-center gap-1.5 px-1 py-1 text-xs text-text-muted">
            <Spinner className="size-3.5" />
            Finding the closers…
          </p>
        ) : rows.length === 0 ? (
          <p className="px-1 py-1 text-xs text-text-muted">
            {closers === undefined
              ? "Could not read who is on the closers' line."
              : search.trim()
                ? `Nobody on the line matches “${search.trim()}”.`
                : needs.length > 0
                  ? `Nobody on the line holds ${needsText}.`
                  : "Nobody staffs the closers' line yet."}
          </p>
        ) : (
          rows.map((row) => (
            <ChoiceRow
              key={row.closer.sip_endpoint_id}
              checked={picked?.sip_endpoint_id === row.closer.sip_endpoint_id}
              disabled={!row.pickable}
              title={row.name}
              subtitle={[row.closer.user, row.status, ...heldChips(row.closer, skills)].join(" · ")}
              onPick={() => setTarget(row.closer)}
            />
          ))
        )}
      </div>
      {ownCallsHelp(rows) && <p className="px-1 text-xs text-text-muted">{ownCallsHelp(rows)}</p>}
      <label htmlFor={id} className="mt-1 text-xs text-text-muted">
        A note for the closer
      </label>
      <Textarea
        id={id}
        rows={3}
        maxLength={2000}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="What happened, what they want, what you told them"
        className="min-h-16 font-sans text-sm"
      />
      <div className="grid grid-cols-2 gap-1.5">
        <Button type="submit" className="h-10" loading={busy}>
          <Headphones />
          Warm transfer
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-10"
          disabled={busy}
          onClick={() => onSend(note, "blind", choice)}
        >
          <Send />
          Blind transfer
        </Button>
      </div>
      <p className="text-xs text-text-muted">{sendHelp(picked ? closerName(picked) : null)}</p>
    </form>
  );
}

/** One choice of who takes the caller. A radio, not a checkbox: the caller goes to one person. */
function ChoiceRow({
  checked, disabled = false, title, subtitle, onPick,
}: {
  checked: boolean;
  disabled?: boolean;
  title: string;
  subtitle: string;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      aria-label={title}
      disabled={disabled}
      onClick={onPick}
      className={cn(
        "flex w-full items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left transition-colors",
        checked ? "border-accent-8 bg-accent-2" : "border-border bg-surface hover:bg-surface-hover",
        "disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-surface",
      )}
    >
      {checked ? (
        <CircleCheck className="size-4 shrink-0 text-accent-11" aria-hidden />
      ) : (
        <Circle className="size-4 shrink-0 text-text-subtle" aria-hidden />
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm text-text">{title}</span>
        <span className="block truncate text-xs text-text-muted">{subtitle}</span>
      </span>
    </button>
  );
}

/* --- the consult a setter asked for (ADR-027) ------------------------------------------------ */

/**
 * The consult this rep has just asked for, while the ACD is still looking for a closer.
 *
 * Module state rather than the dock's, because the request is made in the Transfer panel and the
 * bar that follows it lives on the call screen, which unmounts that panel. It covers the search
 * only: the moment a closer answers, the rep's own record says so and takes over. It names the
 * call as well as the request, so an ask nothing got to forget cannot be read as the next call's.
 */
let pending: { handoffId: string; callId: string } | null = null;
const watchers = new Set<() => void>();

function setPending(next: { handoffId: string; callId: string } | null): void {
  pending = next;
  for (const notify of watchers) notify();
}

export function rememberConsult(handoffId: string, callId: string): void {
  setPending({ handoffId, callId });
}

export function forgetConsult(): void {
  if (pending !== null) setPending(null);
}

/**
 * Whether a request to send this caller to a closer is already out (ADR-058): the one this rep has
 * just made, or the briefing it has turned into. One call has one live request — core-api refuses a
 * second with a 409 — so Transfer says so rather than offering to make one.
 */
export function useConsultOut(callId: string | null): boolean {
  const asked = usePendingConsult();
  const briefing = useBriefing();
  if (callId === null) return false;
  // The briefing is about the call the rep is on, which is the one Transfer is open for.
  return asked?.callId === callId || briefing?.role === "setter";
}

function usePendingConsult(): { handoffId: string; callId: string } | null {
  return React.useSyncExternalStore(
    (notify) => {
      watchers.add(notify);
      return () => {
        watchers.delete(notify);
      };
    },
    () => pending,
    () => pending,
  );
}

/** A handoff nobody is waiting on any more. */
const CONSULT_OVER = ["connected", "cancelled", "abandoned", "failed"];

/**
 * A warm handoff on the setter's call screen: the search for a closer, and then the briefing.
 *
 * What is happening comes from the rep's own record, which names the closer they are briefing. The
 * id the request answered with is only needed before that — while the ACD is still ringing closers
 * and nothing has changed for anybody.
 */
export function ConsultBar() {
  const toast = useToast();
  const asked = usePendingConsult();
  const briefing = useBriefing();
  const mine = briefing?.role === "setter" ? briefing : null;
  const handoffId = mine?.handoffId ?? asked?.handoffId ?? null;
  const { data: handoff } = useHandoff(handoffId, { watch: mine === null });
  const { data: colleagues } = useColleagues({ enabled: handoffId !== null });
  const finish = useFinishConsult();
  const [error, setError] = React.useState<string | null>(null);
  const over = handoff !== undefined && CONSULT_OVER.includes(handoff.status);
  // Who the briefing is with, else who it is for: a closer the setter named (ADR-031).
  const extension = mine?.withExtension ?? handoff?.to_user ?? handoff?.target_user ?? null;
  const closer =
    colleagues?.find((c) => c.extension === extension)?.name ?? extension ?? "a closer";
  const named = handoff?.target_user != null;
  const told = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (mine !== null || !over || handoff === undefined) return;
    forgetConsult();
    // A named closer the ACD could not reach ends the consult on its own (ADR-031): the setter,
    // still on the call, is told why, once.
    const why = unreached(handoff, closer);
    if (why && told.current !== handoff.id) {
      told.current = handoff.id;
      toast.error(why.title, why.detail);
    }
  }, [mine, over, handoff, closer, toast]);

  if (handoffId === null || (mine === null && over)) return null;
  const act = (action: ConsultEnding) => {
    setError(null);
    finish.mutate(
      { handoffId, action },
      {
        onSuccess: () => {
          forgetConsult();
          if (action === "complete") toast.success("Connected", `${closer} has the caller.`);
          else if (action === "join") {
            toast.success("Three-way call", `You, ${closer} and the caller are all on the call.`);
          } else toast.success("Handoff called off", "The caller is back with you.");
        },
        onError: (e) =>
          setError(e instanceof ApiError ? e.message : "Could not do that just now."),
      },
    );
  };

  return (
    <div className="w-full rounded-lg border border-accent-8 bg-surface-sunken px-3 py-2 text-left text-xs">
      {mine ? (
        <>
          <p className="flex min-w-0 items-center gap-1.5 font-medium text-text">
            <Headphones className="size-3.5 shrink-0 text-text-muted" aria-hidden />
            <span className="truncate">You are briefing {closer}</span>
          </p>
          <p className="mt-0.5 text-text-muted">Your caller is on hold and cannot hear you.</p>
          <BriefingEndings
            busy={(ending) => finish.isPending && finish.variables?.action === ending}
            onEnd={act}
          />
        </>
      ) : (
        <>
          <p className="flex items-center gap-1.5 text-text">
            <Spinner className="size-3.5 shrink-0" />
            <span>
              {named
                ? `Waiting for ${closer} to pick up. You are still on the call.`
                : "Looking for a closer. You are still on the call."}
            </span>
          </p>
          <Button
            type="button"
            variant="outline"
            className="mt-2 h-9 w-full"
            loading={finish.isPending}
            onClick={() => act("cancel")}
          >
            <Undo2 />
            Never mind
          </Button>
        </>
      )}
      {error && (
        <p role="alert" className="mt-1.5 text-danger-11">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * The three ways a briefing ends, drawn once for the ACD's bar above and the direct room's
 * (`DirectConsultBar`): Merge all and Take the caller back as buttons, Connect them & leave
 * behind a ⋯ menu. Until 2026-09-24 it was the filled button at the front of the row, and the
 * floor asked for it out of the way: it is the one ending that takes the rep off the call, and a
 * rep reaching for Merge all mid-briefing pressed it. One more click, meant, is the cost.
 *
 * Wrapping rather than a fixed pair of columns, because the dock is 320px and these labels are
 * words, not glyphs: side by side on the Phone page, one above the other in the dock.
 */
export function BriefingEndings({
  busy, onEnd, leave = true,
}: {
  /** Whether that ending is in flight, so its control shows it. */
  busy: (ending: ConsultEnding) => boolean;
  onEnd: (ending: ConsultEnding) => void;
  /** Whether Connect them & leave is offered at all. Not on a campaign call with an outside
   *  participant (ADR-065): the last rep leaving ends the call for the caller. */
  leave?: boolean;
}) {
  return (
    <>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <Button
          type="button"
          className="h-9 min-w-40 flex-1"
          loading={busy("join")}
          onClick={() => onEnd("join")}
        >
          <UsersRound />
          Merge all
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-9 min-w-40 flex-1"
          loading={busy("cancel")}
          onClick={() => onEnd("cancel")}
        >
          <Undo2 />
          Take the caller back
        </Button>
        {leave && (
          <DropdownMenu>
            <Tooltip content="More ways to end the briefing">
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="size-9 shrink-0"
                  aria-label="More ways to end the briefing"
                  loading={busy("complete")}
                >
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
            </Tooltip>
            <DropdownMenuContent>
              <DropdownMenuItem onSelect={() => onEnd("complete")}>
                <PhoneForwarded />
                Connect them &amp; leave
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <p className="mt-1.5 text-text-muted">
        {leave
          ? "Merge all keeps you on the call with them both. Connect them & leave, in the ⋯ menu, hands the caller over and drops you off."
          : "Merge all keeps you on the call with them both. Take the caller back drops the person you brought in."}
      </p>
    </>
  );
}

/* --- a call in its own room (ADR-029) -------------------------------------------------------- */

/** How long a hold the ACD has accepted may take to reach the rep's record before the Hold tile
 *  stops waiting for it. */
const HOLD_SETTLES_MS = 4_000;

/**
 * Hold for a call the ACD connected, which lives in a conference room: through the ACD, which plays
 * the caller their line's music in the room, because the phone's own hold would leave them
 * listening to nobody (`holdsInRoom`).
 *
 * The request is only accepted, so the tile keeps spinning until the rep's record says the caller
 * is held (or no longer is), and stops waiting after a few seconds: a hold the ACD turned down
 * leaves the record as it was.
 */
export function useRoomHold(acd: AcdCall | null): {
  held: boolean;
  pending: boolean;
  toggle: () => void;
} {
  const toast = useToast();
  const hold = useCallHold();
  const [asked, setAsked] = React.useState<boolean | null>(null);
  const held = acd?.callerHeld === true;

  React.useEffect(() => {
    if (asked === null) return;
    if (asked === held) {
      setAsked(null);
      return;
    }
    const timer = window.setTimeout(() => setAsked(null), HOLD_SETTLES_MS);
    return () => window.clearTimeout(timer);
  }, [asked, held]);

  const toggle = () => {
    if (acd === null || asked !== null) return;
    const next = !held;
    setAsked(next);
    hold.mutate(
      { callerChannelId: acd.callerChannelId, held: next },
      {
        onError: (e) => {
          setAsked(null);
          toast.error(
            next ? "Could not hold the caller" : "Could not take the caller off hold",
            e instanceof ApiError ? e.message : "Please try again.",
          );
        },
      },
    );
  };
  return { held, pending: asked !== null, toggle };
}

/* --- a supervisor listening in (ADR-029) ----------------------------------------------------- */

/** The calls this tab's supervisor is listening in on (`lib/monitor.ts`). */
export function useMonitors(): ReadonlyMap<string, Monitoring> {
  return React.useSyncExternalStore(subscribeMonitors, currentMonitors, currentMonitors);
}

/** Whether `callId` is a listen-in this phone is joining by itself (`joiningListenIn`). */
export function useJoiningListenIn(callId: string | null): boolean {
  const phone = useSoftphone();
  const monitors = useMonitors();
  const asking = React.useSyncExternalStore(subscribeMonitors, monitorAsked, monitorAsked);
  return callId !== null && joiningListenIn(phone.calls, monitors, callId, asking);
}

/**
 * A supervisor's call into a rep's call, answered without a click and whatever this browser's own
 * auto-answer says: they asked for it a moment ago on the floor (`monitorToAnswer`). The listen-in
 * they were already on is hung up first. Forgotten again once that call has come and gone
 * (`endedMonitors`). Mounted once, in the dock, beside the campaign's auto-answer.
 */
export function useMonitorAutoAnswer(): void {
  const phone = useSoftphone();
  const toast = useToast();
  const monitors = useMonitors();
  const plan = monitorToAnswer(phone.calls, monitors, phone.autoAnswer);
  const answered = React.useRef(new Set<string>());
  const seen = React.useRef(new Set<string>());
  // The plan as the effect's key: a new array each render must not re-run it.
  const target = plan?.callId ?? null;
  const ending = plan?.end.join(",") ?? "";

  const { answer, endAndAnswer, hangup, activeCallId, calls } = phone;
  React.useEffect(() => {
    if (target === null || answered.current.has(target)) return;
    answered.current.add(target);
    const earlier = ending ? ending.split(",") : [];
    void (async () => {
      // `endAndAnswer` hangs up the call the supervisor is on and waits for it to go before
      // answering; any other earlier listen-in is hung up here first.
      for (const id of earlier) if (id !== activeCallId) await hangup(id);
      await (activeCallId !== null && earlier.includes(activeCallId) ? endAndAnswer(target) : answer(target));
    })().catch((e: unknown) => {
      console.error("[softphone] monitor auto-answer failed", e);
      toast.error(
        "Could not join the call",
        "Check that this browser may use the microphone, then press Stop supervising and try again.",
      );
    });
  }, [target, ending, activeCallId, answer, endAndAnswer, hangup, toast]);

  React.useEffect(() => {
    for (const id of endedMonitors(calls, monitors, seen.current)) forgetMonitor(id);
  }, [calls, monitors]);
}

/**
 * On the supervisor's own call into a rep's call: whose call it is, who can hear them, and the three
 * ways to take part. Stop supervising, the phone's end-call button on this call, is how they leave;
 * the rep's call goes on.
 */
export function MonitorBar({ callId }: { callId: string }) {
  const monitor = useMonitors().get(callId);
  const change = useMonitorMode();
  const [error, setError] = React.useState<string | null>(null);
  if (!monitor) return null;

  const choose = (mode: MonitorMode) => {
    if (mode === monitor.mode || change.isPending) return;
    setError(null);
    change.mutate(
      { monitorId: monitor.monitorId, mode },
      {
        onSuccess: () => setMonitorMode(monitor.monitorId, mode),
        onError: (e) =>
          setError(e instanceof ApiError ? e.message : "Could not change that just now."),
      },
    );
  };
  const current = MONITOR_MODES.find((m) => m.mode === monitor.mode) ?? MONITOR_MODES[0]!;

  return (
    <div className="w-full rounded-lg border border-accent-8 bg-surface-sunken px-3 py-2 text-left text-xs">
      <p className="flex min-w-0 items-center gap-1.5 font-medium text-text">
        <Ear className="size-3.5 shrink-0 text-text-muted" aria-hidden />
        <span className="truncate">{monitor.repName}'s call</span>
      </p>
      <p className="mt-0.5 text-text-muted" aria-live="polite">
        {current.hint}
      </p>
      <div className="mt-2 grid grid-cols-3 gap-1.5" role="group" aria-label="How you take part">
        {MONITOR_MODES.map((m) => (
          <Button
            key={m.mode}
            type="button"
            variant={m.mode === monitor.mode ? "default" : "outline"}
            className="h-9 px-2"
            aria-pressed={m.mode === monitor.mode}
            loading={change.isPending && change.variables?.mode === m.mode}
            onClick={() => choose(m.mode)}
          >
            {m.label}
          </Button>
        ))}
      </div>
      {error && (
        <p role="alert" className="mt-1.5 text-danger-11">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Records the outcome the voicemail suggestion planned (ADR-106) when the call's window opens,
 * from the dock, which is on every screen.
 *
 * The window does the same when it is drawn (`DispositionView`). But the suggestion opens from the
 * phone's rail, so a rep can hang up from it with the phone closed, and an outcome that waited for
 * the phone to be opened would run out of window. Whichever of the two sees the call first
 * records it (`claimPlannedOutcome`).
 */
export function usePlannedOutcomeRecorder(inWindow: boolean): void {
  const { user } = useAuth();
  const planned = usePlannedOutcome();
  const watching = inWindow && planned !== null;
  const { data: me } = useAcdMe({ enabled: Boolean(user?.sip_endpoint_id) });
  const agent = watching && me ? { user: me.user, domain: me.domain } : null;
  const sessions = useMyAcdSessions(agent);
  const session = pendingDisposition(sessions.data ?? [], agent);
  const outcomes = useCallDispositions(session?.campaign_id ?? null, session !== null);
  const record = useDispositionCall();
  const auto = plannedOutcomeFor(planned, session, outcomes.data ?? []);
  const callId = session?.channel_uuid ?? null;
  const slug = auto?.slug ?? null;
  const offered = outcomes.data !== undefined;
  React.useEffect(() => {
    if (callId === null || slug === null || !offered) return;
    if (!claimPlannedOutcome(callId)) return;
    record.mutate(
      { callId, disposition: slug },
      { onSettled: () => forgetPlannedOutcome(callId) },
    );
    // `record` is stable for this purpose: the call and the outcome are what start it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callId, slug, offered]);
}

/**
 * The line's disposition window: the call has ended, and the ACD waits for its outcome before it
 * offers the rep another (ADR-017).
 *
 * One click records an outcome: a confirmation after every call is how windows run out. An outcome
 * that has something to ask first asks it in a window of its own over the page: the note the
 * campaign wants for it, and for a callback on a call with a lead its day and time (ADR-080),
 * opening on the outcome's own wait so a hurried rep only confirms (Enter schedules it). Both were
 * forms under the outcomes, and on the Phone page the band the outcomes are in is held to part of
 * the page, so the note and the button to record were under its fold.
 * The call comes from the rep's newest queue session rather than the live record, which forgets a
 * call the moment it ends. Once an outcome is recorded it stays on screen until the ACD closes the
 * window, so a slow broker never reads as a click that did nothing.
 *
 * "Call dropped" on a call with a lead also puts the rep on the phone's own break, Calling back,
 * and remembers who to call (`lib/call-back.ts`): the dock then offers the call back, and the
 * dialer cannot hand the rep another lead first (ADR-085).
 *
 * A callback time the rep picked on the call (ACL-559, `softphone/call-scheduling.tsx`) is what a
 * callback's window opens on, in place of the outcome's own wait; it is said above the outcomes, and
 * goes once an outcome is recorded. Any other outcome leaves it unused, and makes no callback.
 *
 * A callback may carry up to four backup times (ACL-559, ADR-183 and ADR-210 in `platform`), for when
 * the dialer's call at the time before reaches nobody. They go with the outcome; an older core-api
 * drops them, or all but the first, without a word, and the window says so when the answer does not
 * echo them. A lead who already has a callback to come
 * is told this one replaces it, with the old time offered as the backup or as the first.
 *
 * On a dialer call whose cadence call has a redial left, the window says which outcomes send it
 * (`redialHint`, ADR-105): the dialer calls the same lead again at once for this rep after an
 * outcome that says nobody was reached, and a rep who does not know reads it as a fault.
 */
export function DispositionView() {
  const { user } = useAuth();
  const sees = seesCampaigns(user?.role);
  const { data: me } = useAcdMe({ enabled: Boolean(user?.sip_endpoint_id) });
  const agent = me ? { user: me.user, domain: me.domain } : null;
  const sessions = useMyAcdSessions(agent);
  const session = pendingDisposition(sessions.data ?? [], agent);
  const outcomes = useCallDispositions(session?.campaign_id ?? null, session !== null);
  // The stages this campaign keeps calling on a cadence of their own, for the icon on an outcome
  // that would otherwise read "no more calls" (`keepsCalling`).
  const withCadence = useStagesWithCadence(session?.campaign_id ?? null, session !== null);
  const record = useDispositionCall();
  // The lead's clock, for a callback's time: the card the phone already reads during the call.
  const card = useLeadCard(session?.lead_id ?? null, session?.session_id ?? null);
  const callback = useCallbackTime(card.data?.clock_timezone ?? card.data?.timezone ?? null);
  const backup = useBackupTime(callback.value, [], backupGapMinutes(card.data?.callback_window_minutes));
  const soon = useCallbackMinutes();
  const toast = useToast();
  const noteId = React.useId();
  // The outcome that asks for something before it is recorded (a note, a callback's time, or
  // both), and whether its window is open. The outcome is kept when the window closes, so the
  // window still names it on its way out.
  const [asked, setAsked] = React.useState<Disposition | null>(null);
  const [asking, setAsking] = React.useState(false);
  const [note, setNote] = React.useState("");
  const [recorded, setRecorded] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  // The case the call was about (ADR-223): the record the ACD attached, unless the rep says one of
  // the person's other open cases. Forgotten with the call.
  const [about, setAbout] = React.useState<string | null>(null);
  const cases = casesOnTheNumber(card.data);
  const callKey = session?.channel_uuid ?? null;
  // Callbacks for the others in the accident, booked after the caller's (ACL-671). Forgotten with the call.
  const [also, setAlso] = React.useState<AlsoBooking[]>([]);
  const bookOther = useContactOutcome();
  React.useEffect(() => {
    setAbout(null);
    setAlso([]);
  }, [callKey]);
  const rememberCallBack = useSetCallBackMemory();
  const act = useAgentAction();
  // The outcome the voicemail suggestion chose on this call (ADR-106): recorded here without
  // asking, when the campaign offers it. One that it does not offer is dropped, and the window
  // asks as it always does.
  const planned = usePlannedOutcome();
  const auto = plannedOutcomeFor(planned, session, outcomes.data ?? []);
  const autoRecorded = React.useRef<string | null>(null);
  // The callback time picked during the call (ACL-559), for a callback's window to open on.
  const pickedTime = usePickedCallback(session?.channel_uuid ?? null);
  // Which attempt of its call a dialer call was, and the redial an outcome may send (ADR-105).
  // Asked only for a lead the dialer reached: no other call has a dial behind it.
  const dialed = session !== null && session.kind === "outbound";
  const attempt = useCallDetection(session?.channel_uuid ?? null, dialed).data;
  // The rep's own most used outcomes on this campaign, for the suggested row (ADR-160).
  const mine = useMyOutcomes(session?.campaign_id ?? null, session !== null);

  const choose = (
    outcome: Disposition,
    withNote?: string,
    callbackAt?: string,
    callbackPurpose?: string | null,
    callbackBackupTimes: readonly string[] = [],
  ) => {
    if (!session) return;
    setError(null);
    record.mutate(
      {
        callId: session.channel_uuid, disposition: outcome.slug, note: withNote, callbackAt, callbackPurpose,
        callbackBackupTimes, leadId: about !== null && about !== session.lead_id ? about : undefined,
      },
      {
        onSettled: () => forgetPlannedOutcome(session.channel_uuid),
        onSuccess: async (result) => {
          forgetPickedCallback(session.channel_uuid);
          const lead = callback.value.leadClock ? callback.value.zone : null;
          // The others' callbacks, each on their own record through the record's own route, after the
          // caller's is in (ACL-671). A refusal of one is said, never thrown: the caller's outcome stands.
          if (callbackAt && also.length > 0) {
            const results: { name: string; when: string | null; problem: string | null }[] = [];
            for (const booking of also) {
              try {
                const booked = await bookOther.mutateAsync({
                  leadId: booking.leadId, disposition: outcome.slug, callbackAt: booking.iso,
                  callbackPurpose, callbackBackupTimes: [],
                });
                results.push({ name: booking.name, when: booked.callback_at ? callbackWhen(booked.callback_at, lead).there : null, problem: null });
              } catch (e) {
                results.push({ name: booking.name, when: null, problem: e instanceof ApiError ? e.message : "could not be booked." });
              }
            }
            const said = alsoBookedMessage(results);
            if (said) toast[said.tone](said.title, said.detail);
          }
          const when = result.callback_at ? callbackWhen(result.callback_at, lead).there : null;
          const kept = keptBackups(result);
          const backupWhen =
            kept.length > 0 && result.callback_at
              ? backupTimesWhen(kept, lead, Date.parse(result.callback_at))
              : null;
          setRecorded(when ? `${outcome.label} for ${when}${backupWhen ? `, or else ${backupWhen}` : ""}` : outcome.label);
          // An older core-api takes the callback and drops its backups, or all but the first,
          // without a word: only the answer's echo says what was kept.
          const saved = callbackAt ? backupsKept(callbackBackupTimes, result) : "all";
          if (saved === "none") {
            toast.warning(
              "The backup time was not saved",
              "This server does not take one yet. The callback is set.",
            );
          } else if (saved === "first") {
            toast.warning(
              "Only the first backup time was saved",
              "This server keeps one backup time. The callback is set.",
            );
          }
          if (outcome.slug === DROPPED_OUTCOME && session.lead_id && session.caller_number) {
            // The way back (ADR-085): remember who, and take the phone's own break so no other
            // lead is handed to the rep before they have called.
            rememberCallBack({
              leadId: session.lead_id,
              name: session.caller_name ?? formatPhoneNumber(session.caller_number),
              phone: session.caller_number,
              campaignId: session.campaign_id,
              campaignName: sees
                ? (me?.lines.find((l) => l.name === session.queue_name)?.campaign_name ?? null)
                : null,
              since: Date.now(),
            });
            act.mutate([{ action: "pause", queue: null, code: CALLING_BACK.code }]);
          }
        },
        onError: (e) =>
          setError(e instanceof ApiError ? e.message : "Could not record the outcome."),
      },
    );
  };

  React.useEffect(() => {
    if (!session || outcomes.data === undefined) return;
    if (auto && autoRecorded.current !== session.channel_uuid) {
      autoRecorded.current = session.channel_uuid;
      // Once per call: this window can be on screen twice, and the dock records for a closed phone.
      if (claimPlannedOutcome(session.channel_uuid)) choose(auto);
    } else if (!auto && planned?.callId === session.channel_uuid) {
      forgetPlannedOutcome(session.channel_uuid);
    }
    // `choose` is this render's closure over the same session: the call's id is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.channel_uuid, auto?.slug, planned?.callId, outcomes.data]);

  if (recorded) {
    return (
      <p role="status" className="py-2 text-center text-xs text-text-muted">
        <Spinner className="mr-1 inline size-3 align-[-2px]" />
        {recorded} is recorded. Your line opens again in a moment.
      </p>
    );
  }
  if (auto && !error) {
    return (
      <p role="status" className="py-2 text-center text-xs text-text-muted">
        <Spinner className="mr-1 inline size-3 align-[-2px]" />
        Recording {auto.label}.
      </p>
    );
  }
  if (sessions.error) {
    return (
      <Callout tone="danger">
        Could not find the call to record:{" "}
        {sessions.error instanceof ApiError ? sessions.error.message : "please try again."}
      </Callout>
    );
  }
  if (sessions.isPending || (session !== null && outcomes.isPending)) {
    return (
      <div className="flex justify-center py-4">
        <Spinner className="size-4" />
      </div>
    );
  }
  if (!session) {
    return (
      <p className="py-2 text-center text-xs text-text-muted">
        Your last call's outcome is recorded. Your line opens again when its window closes.
      </p>
    );
  }

  // The outcome's window put away with nothing recorded. What it said had gone wrong goes with
  // it: under the outcomes it would read as a refusal of whatever is pressed next.
  const stopAsking = () => {
    setAsking(false);
    setError(null);
  };
  // A callback on a call with a lead is promised for a time; an anonymous caller has no record
  // to call back, so theirs is recorded as it always was.
  const isTimed = (outcome: Disposition) => outcome.kind === "callback" && session.lead_id !== null;
  const leadClock = callback.value.leadClock ? callback.value.zone : null;
  const askedTimed = asked !== null && isTimed(asked);
  const noteMissing = asked !== null && asked.requires_note && !note.trim();
  // Not qualified asks why: one outcome stands for every reason, and the rep types it (ACL-574).
  const asksWhy = asked !== null && asked.requires_note && asked.outcome_group === "not_qualified";
  const cannotRecord = noteMissing || (askedTimed && (card.isPending || !callback.value.iso || !backup.ok));
  const line = me?.lines.find((l) => l.name === session.queue_name);
  const caller =
    session.caller_name ??
    (session.caller_number ? formatPhoneNumber(session.caller_number) : "The caller");
  const through = sees ? ` · ${line?.campaign_name ?? session.queue_name}` : "";
  // What the platform records by itself is not this window's to offer (ADR-160).
  const offered = (outcomes.data ?? []).filter((o) => o.rep_can_pick !== false);
  const hidden = (outcomes.data?.length ?? 0) - offered.length;
  const redial = attempt?.call_id === session.channel_uuid ? attempt : null;
  const hint = redialHint(redial, offered, session.caller_name?.trim().split(/\s+/)[0] ?? null);
  // After a short dialer call the rep most likely reached nobody: those picks come first.
  const short = dialed && (session.talk_seconds ?? 0) < SHORT_CALL_SECONDS;
  const suggested = suggestedOutcomes(offered, mine.data ?? [], { nobodyFirst: short });
  const choice = (outcome: Disposition) => {
    const timed = isTimed(outcome);
    if (!timed && !outcome.requires_note) {
      setAsking(false);
      choose(outcome);
      return;
    }
    setError(null);
    setAsked(outcome);
    setAsking(true);
    if (timed && pickedTime) {
      callback.set(wallTime(pickedTime.at, callback.value.zone));
      callback.setPurpose(pickedTime.purpose);
      backup.setInstants(pickedTime.backupTimes);
    } else if (timed) {
      callback.start(outcome);
      backup.clear();
    }
  };

  return (
    <div className="flex flex-col gap-2.5">
      <div className="min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-semibold text-text">How did the call go?</p>
          <CallTypeChip type={callTypeOf(session.kind)} />
        </div>
        <p className="truncate text-xs text-text-muted">
          {caller}{through}
          {redial?.redial && redial.redial.n > 0
            ? ` · redial ${redial.redial.n} of ${Math.max(redial.redial.of, redial.redial.n)}`
            : ""}
        </p>
      </div>

      {pickedTime && session.lead_id !== null && (
        <p className="flex items-start gap-1.5 rounded-md border border-accent-4 bg-accent-2 px-2.5 py-1.5 text-xs text-text">
          <CalendarClock className="mt-0.5 size-3.5 shrink-0 text-accent-11" aria-hidden />
          <span className="min-w-0">
            Callback time picked on the call: {callbackWhen(pickedTime.at, leadClock).there}
            {pickedTime.backupTimes.length > 0 &&
              ` · or else ${backupTimesWhen(pickedTime.backupTimes, leadClock, pickedTime.at)}`}
            . Choose
            a callback outcome to schedule it.
          </span>
        </p>
      )}

      {hint && (
        <p className="flex items-start gap-1.5 rounded-md border border-accent-4 bg-accent-2 px-2.5 py-1.5 text-xs text-text">
          <RotateCcw className="mt-0.5 size-3.5 shrink-0 text-accent-11" aria-hidden />
          {hint}
        </p>
      )}

      {cases.length > 0 && session.lead_id !== null && (
        <fieldset className="mb-2 rounded-md border border-border px-2.5 py-2" aria-label="This call was about">
          <legend className="px-1 text-2xs font-semibold uppercase tracking-wide text-text-subtle">This call was about</legend>
          <div className="flex flex-col gap-1">
            {[{ id: session.lead_id, label: `${(card.data ? leadName(card.data) : null) ?? "This record"} · the record on the call` },
              ...cases.map((c) => ({ id: c.id, label: caseOptionLabel(c) }))].map((option) => (
              <label key={option.id} className="flex cursor-pointer items-center gap-2 text-xs text-text">
                <input
                  type="radio"
                  name="call-about"
                  value={option.id}
                  checked={(about ?? session.lead_id) === option.id}
                  disabled={record.isPending}
                  onChange={() => setAbout(option.id)}
                />
                <span className="min-w-0 truncate">{option.label}</span>
              </label>
            ))}
          </div>
        </fieldset>
      )}

      {outcomes.error ? (
        <Callout tone="danger">
          {outcomes.error instanceof ApiError
            ? outcomes.error.message
            : "Could not load the outcomes to choose from."}
        </Callout>
      ) : offered.length === 0 ? (
        <p className="text-xs text-text-muted">
          {sees
            ? "This campaign offers no outcomes to choose. An admin picks them on the campaign's Dispositions tab."
            : "There are no outcomes to choose. Tell your supervisor."}
        </p>
      ) : (
        <OutcomePicker
          offered={offered}
          withCadence={withCadence}
          suggested={suggested}
          why={
            short
              ? "a short dialer call, so your picks when nobody was reached come first"
              : sees
                ? "your most used on this campaign this week"
                : "your most used this week"
          }
          hidden={hidden}
          litId={asking ? (asked?.id ?? null) : null}
          disabled={record.isPending}
          onPick={choice}
        />
      )}

      {/* What an outcome asks before it is recorded, over the page: the outcomes stay where they
          are under it. Drawn from in here, it is part of the phone's own popover to React, so the
          docked phone does not take the focus it gets for a press somewhere else and close under
          it. */}
      <Dialog
        open={asking}
        onOpenChange={(open) => {
          if (!open) stopAsking();
        }}
      >
        {asked && (
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {askedTimed ? "Schedule a callback" : asksWhy ? "Why not qualified?" : "Add a note"}
              </DialogTitle>
              <DialogDescription>
                {caller}{through}
              </DialogDescription>
            </DialogHeader>
            <DialogForm
              aria-label={
                askedTimed
                  ? `When to call back for ${asked.label}`
                  : asksWhy
                    ? "Why the lead is not qualified"
                    : `A note for ${asked.label}`
              }
              onSubmit={(e) => {
                e.preventDefault();
                if (cannotRecord) return;
                // A note goes only with the outcome that asked for it. One typed for another
                // outcome and cancelled is kept for that window, and this one has no box for it.
                const withNote = asked.requires_note ? note.trim() || undefined : undefined;
                choose(
                  asked,
                  withNote,
                  askedTimed ? (callback.value.iso ?? undefined) : undefined,
                  askedTimed ? callback.purposeToSend : null,
                  askedTimed ? backup.isos : [],
                );
              }}
            >
              <DialogBody className="flex flex-col gap-3">
                {askedTimed &&
                  (card.isPending ? (
                    <div className="flex justify-center py-2">
                      <Spinner className="size-4" />
                    </div>
                  ) : (
                    <CallbackTimes
                      leadId={card.data?.id ?? null}
                      outcome={asked.slug}
                      callback={callback}
                      backup={backup}
                      soon={soon}
                      disabled={record.isPending}
                      withPurpose
                      windowMinutes={card.data?.callback_window_minutes}
                      open={card.data?.callback}
                    />
                  ))}
                {askedTimed && (
                  <AlsoBookFor
                    leadId={card.data?.id ?? null}
                    caller={card.data}
                    outcome={asked}
                    disabled={record.isPending || bookOther.isPending}
                    onChange={setAlso}
                  />
                )}
                {asked.requires_note && (
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor={noteId} className="text-xs text-text-muted">
                      {asksWhy ? "Why is this lead not qualified?" : `A note for ${asked.label}`}
                    </label>
                    <Textarea
                      id={noteId}
                      rows={askedTimed ? 3 : 4}
                      maxLength={2000}
                      value={note}
                      placeholder={asksWhy ? "Has an attorney, at fault, past the statute of limitations…" : undefined}
                      onChange={(e) => setNote(e.target.value)}
                      className="min-h-16 font-sans text-sm"
                    />
                  </div>
                )}
                {error && <Callout tone="danger">{error}</Callout>}
              </DialogBody>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={stopAsking}>
                  Cancel
                </Button>
                <Button type="submit" variant="default" loading={record.isPending || bookOther.isPending} disabled={cannotRecord}>
                  {askedTimed && also.length > 0
                    ? `Schedule ${also.length + 1} callbacks`
                    : `${askedTimed ? "Schedule" : "Record"} ${asked.label}`}
                </Button>
              </DialogFooter>
            </DialogForm>
          </DialogContent>
        )}
      </Dialog>

      {error && !asking && <Callout tone="danger">{error}</Callout>}
    </div>
  );
}
